import { useState } from "react";

import { cva } from "class-variance-authority";

import {
  PlanningScope,
  PlanningType,
  type FullAirHandlerUnit,
  type PlanningException,
  type PutPlanningBody,
} from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Circle, DateField, Heading, Select } from "@eisox/design-system";
import { BinIcon, PlusIcon } from "@eisox/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { airHandlerUnitApi, planningApi } from "~/api";
import { m } from "~/paraglide/messages";

import { airHandlerUnitsToPlaces } from "../helpers";
import { CopyExceptionalPlanningDialog } from "./copy-exceptional-planning-dialog";

type ExceptionType = PlanningType.OCCUPATION | PlanningType.INOCCUPATION | PlanningType.STOP;

const EXCEPTION_TYPES: readonly ExceptionType[] = [
  PlanningType.OCCUPATION,
  PlanningType.INOCCUPATION,
  PlanningType.STOP,
];

const typeLabels: Record<ExceptionType, string> = {
  [PlanningType.OCCUPATION]: m.occupation(),
  [PlanningType.INOCCUPATION]: m.inoccupation(),
  [PlanningType.STOP]: m.holidays(),
};

// The add row is filled progressively, so its dates are optional until submitted (unlike the DTO).
type DraftPeriod = { type: PlanningType; begin?: string; end?: string };

interface ExceptionalPlanningProps {
  buildingId: string;
  airHandlerUnit: FullAirHandlerUnit[];
  airHandlerUnits: FullAirHandlerUnit[];
  planningExceptions: PlanningException[];
}

export const ExceptionalPlanning: React.FC<ExceptionalPlanningProps> = ({
  buildingId,
  airHandlerUnit,
  airHandlerUnits,
  planningExceptions,
}) => {
  const queryClient = useQueryClient();
  const groupIds = airHandlerUnit.map(ahu => ahu.id);

  const { mutateAsync } = useMutation({
    mutationFn: (body: PutPlanningBody) => planningApi.update(buildingId, body),
    onSuccess: async (_, request) => {
      await Promise.all(
        request.parentIds.map(airHandlerUnitId =>
          queryClient.invalidateQueries({ queryKey: airHandlerUnitApi.queries.plannings(airHandlerUnitId).queryKey }),
        ),
      );
    },
  });

  const source = airHandlerUnitsToPlaces(airHandlerUnit)[0];
  const places = airHandlerUnitsToPlaces(airHandlerUnits);

  const putExceptions = (parentIds: string[], exceptions: PlanningException[]) =>
    mutateAsync({ scope: PlanningScope.AIR_HANDLER_UNIT, parentIds, planningExceptions: exceptions });

  const handlePeriodChange = (index: number, period: PlanningException) =>
    putExceptions(
      groupIds,
      planningExceptions.map((p, i) => (i === index ? period : p)),
    );

  const handleRemovePeriod = (index: number) =>
    putExceptions(
      groupIds,
      planningExceptions.filter((_, i) => i !== index),
    );

  const handleAddPeriod = (period: PlanningException) => putExceptions(groupIds, [...planningExceptions, period]);

  const handleCopy = (targetIds: string[]) => putExceptions(targetIds, planningExceptions);

  return (
    <div className="max-w-162.5 flex flex-col">
      <div className="mb-5 flex items-center justify-between">
        <Heading>{m.air_handler_unit_exceptional_planning()}</Heading>
        <div className="gap-2.5 pl-20 flex items-center [&_svg]:text-primary-blue">
          {source && (
            <CopyExceptionalPlanningDialog
              source={source}
              places={places}
              onCopy={targetIds => {
                void handleCopy(targetIds);
              }}
            />
          )}
        </div>
      </div>
      <div>
        {planningExceptions.map((exception, index) => (
          <PeriodRow
            key={index}
            period={exception}
            onPeriodChange={period => {
              void handlePeriodChange(index, period);
            }}
            onRemove={() => {
              void handleRemovePeriod(index);
            }}
          />
        ))}
        <AddPeriodRow onAdd={handleAddPeriod} />
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * PlanningTypeSelect
 * -----------------------------------------------------------------------------------------------*/

const typeIndicator = cva(
  "before:mr-2.5 before:h-4 before:w-4 before:rounded-xs before:inline-block before:shrink-0 before:align-middle before:content-['']",
  {
    variants: {
      type: {
        [PlanningType.OCCUPATION]: "before:bg-planning-comfort",
        [PlanningType.INOCCUPATION]: "before:bg-planning-absence",
        [PlanningType.STOP]: "before:bg-planning-stop",
      },
    },
  },
);

interface PlanningTypeSelectProps {
  value?: PlanningType;
  onValueChange: (value: PlanningType) => void;
}

const isExceptionType = (value: string): value is ExceptionType =>
  (EXCEPTION_TYPES as readonly string[]).includes(value);

const PlanningTypeSelect: React.FC<PlanningTypeSelectProps> = ({ value, onValueChange }) => (
  <Select.Root
    multiple={false}
    value={value}
    onValueChange={v => {
      if (isExceptionType(v)) onValueChange(v);
    }}
  >
    <Select.Trigger className="w-full">
      <Select.Value className={value && isExceptionType(value) ? typeIndicator({ type: value }) : undefined} />
    </Select.Trigger>
    <Select.Content>
      <Select.Viewport>
        {EXCEPTION_TYPES.map(type => (
          <Select.Item key={type} value={type} className={typeIndicator({ type })}>
            {typeLabels[type]}
          </Select.Item>
        ))}
      </Select.Viewport>
    </Select.Content>
  </Select.Root>
);

/* -------------------------------------------------------------------------------------------------
 * PeriodRow
 * -----------------------------------------------------------------------------------------------*/

const isRangeValid = (begin?: string, end?: string) => !begin || !end || dayjs(begin).isSameOrBefore(dayjs(end));

const rangeError = (begin?: string, end?: string) =>
  isRangeValid(begin, end) ? undefined : m.air_handler_unit_exceptional_planning_range_error();

interface PeriodRowProps {
  period: PlanningException;
  onPeriodChange: (period: PlanningException) => void;
  onRemove: VoidFunction;
}

const PeriodRow: React.FC<PeriodRowProps> = ({ period, onPeriodChange, onRemove }) => {
  const error = rangeError(period.begin, period.end);

  return (
    <div className="mb-2.5 gap-1.25 flex flex-col">
      <div className="gap-2.5 grid grid-cols-[1fr_1fr_16px_1fr_20px] items-center">
        <PlanningTypeSelect value={period.type} onValueChange={type => onPeriodChange({ ...period, type })} />
        <DateField.Root
          format="L - LT"
          value={period.begin ? dayjs(period.begin) : null}
          onValueChange={begin => begin && onPeriodChange({ ...period, begin: begin.toISOString() })}
        >
          <DateField.Input />
        </DateField.Root>
        <span>-</span>
        <DateField.Root
          format="L - LT"
          value={period.end ? dayjs(period.end) : null}
          onValueChange={end => {
            if (end && isRangeValid(period.begin, end.toISOString()))
              onPeriodChange({ ...period, end: end.toISOString() });
          }}
        >
          <DateField.Input />
        </DateField.Root>
        <BinIcon className="cursor-pointer" role="button" aria-label={m.delete()} onClick={onRemove} />
      </div>
      {error && <p className="text-sm text-primary-red">{error}</p>}
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * AddPeriodRow
 * -----------------------------------------------------------------------------------------------*/

const emptyPeriod: DraftPeriod = { type: PlanningType.INOCCUPATION, begin: undefined, end: undefined };

interface AddPeriodRowProps {
  onAdd: (period: PlanningException) => Promise<unknown>;
}

const AddPeriodRow: React.FC<AddPeriodRowProps> = ({ onAdd }) => {
  const [period, setPeriod] = useState<DraftPeriod>(emptyPeriod);

  const { begin, end } = period;
  const error = rangeError(begin, end);
  const canAdd = begin !== undefined && end !== undefined && !error;

  const handleAdd = async () => {
    if (begin === undefined || end === undefined || error) return;
    await onAdd({ type: period.type, begin, end });
    setPeriod(emptyPeriod);
  };

  return (
    <div className="gap-1.25 flex flex-col">
      <div className="gap-2.5 grid grid-cols-[1fr_1fr_16px_1fr_20px] items-center">
        <PlanningTypeSelect value={period.type} onValueChange={type => setPeriod(p => ({ ...p, type }))} />
        <DateField.Root
          format="L - LT"
          value={period.begin ? dayjs(period.begin) : null}
          onValueChange={begin => begin && setPeriod(p => ({ ...p, begin: begin.toISOString() }))}
        >
          <DateField.Input />
        </DateField.Root>
        <span>-</span>
        <DateField.Root
          format="L - LT"
          value={period.end ? dayjs(period.end) : null}
          onValueChange={end => end && setPeriod(p => ({ ...p, end: end.toISOString() }))}
        >
          <DateField.Input />
        </DateField.Root>
        <Circle.Root
          size={20}
          backgroundColor="blue"
          color="white"
          onClick={() => {
            void handleAdd();
          }}
          disabled={!canAdd}
          style={{ cursor: canAdd ? "pointer" : "default" }}
        >
          <PlusIcon />
        </Circle.Root>
      </div>
      {error && <p className="text-sm text-primary-red">{error}</p>}
    </div>
  );
};

export type { ExceptionalPlanningProps };
